import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonReorder,
  IonReorderGroup,
  ItemReorderEventDetail,
  IonFooter,
  IonAlert,
  IonIcon,
  IonToast,
  IonText,
} from "@ionic/react";
import React, { Fragment, memo, useContext, useEffect, useState } from "react";
import UserPreferencesContext, {
  UserPreferences,
} from "../../../components/contexts/UserPreferencesContext";
import { tradingGridGroupGetter } from "../../../utils/commonUtils";
import { saveUserPreferences } from "../../../utils/core-utils/preferenceUtils";
import { checkmarkCircleOutline, closeOutline } from "ionicons/icons";

type EditGridsModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  currentSelectedProfile: number | undefined;
  tradingGridGroup: any;
};

interface ReorderEndCustomEvent extends CustomEvent {
  detail: ItemReorderEventDetail;
  target: HTMLIonReorderGroupElement;
}

const EditGridsModalComponent: React.FC<EditGridsModalProps> = ({
  isOpen,
  setIsOpen,
  currentSelectedProfile,
  tradingGridGroup,
}) => {
  const { userPreferences, setUserPreferences } = useContext(
    UserPreferencesContext,
  );
  const [items, setItems] = useState([]);
  const [isArrangeSaved, setIsArrangeSaved] = useState(false);
  const [hasUserArranged, setHasUserArranged] = useState(false);
  const [isCloseAlertOpen, setIsCloseAlertOpen] = useState(false);

  const currentGridGroups: any[] = JSON.parse(
    userPreferences?.tradinggridgroupsorder || "[]",
  );

  function handleReorderEnd(event: ReorderEndCustomEvent) {
    setItems(event.detail.complete(items));
    setHasUserArranged(true);
  }

  const handleApplyChanges = () => {
    const targetObject = currentGridGroups.find(
      (item: any) => item?.p === currentSelectedProfile,
    );

    if (targetObject) {
      targetObject.g = items;
    }

    const updatedPreferences: UserPreferences = {
      ...userPreferences,
      tradinggridgroupsorder: JSON.stringify(currentGridGroups),
    };

    setUserPreferences(updatedPreferences);

    saveUserPreferences(updatedPreferences);
    setIsOpen(false);
    setIsArrangeSaved(true);
  };

  const handleClose = () => {
    if (hasUserArranged) {
      setIsCloseAlertOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setItems(
        JSON.parse(userPreferences?.tradinggridgroupsorder || "[]").find(
          (item: any) => item?.p === currentSelectedProfile,
        )?.g,
      );
    } else {
      setItems([]);
      setHasUserArranged(false);
    }
  }, [isOpen, userPreferences?.tradinggridgroupsorder]);

  return (
    <Fragment>
      <IonModal isOpen={isOpen}>
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>
              <p className="ion-no-margin text-bold">EDIT MARKET</p>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleClose}>
                <IonIcon
                  icon={closeOutline}
                  color="dark"
                  slot="icon-only"
                ></IonIcon>
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText>
            <h6
              className="ion-no-margin"
              style={{ fontWeight: "bold", marginBottom: "10px" }}
            >
              Arrange Tabs
            </h6>
          </IonText>
          <IonList>
            <IonReorderGroup
              disabled={false}
              onIonItemReorder={handleReorderEnd}
            >
              {items?.map((item: any) => {
                const gridData = tradingGridGroupGetter(tradingGridGroup, item);
                if (!gridData) return null;
                return (
                  <IonItem key={item}>
                    <IonLabel>{gridData.n}</IonLabel>
                    <IonReorder slot="end"></IonReorder>
                  </IonItem>
                );
              })}
            </IonReorderGroup>
          </IonList>
        </IonContent>
        <IonFooter className="ion-no-border ion-padding">
          <IonButton
            onClick={handleApplyChanges}
            expand="block"
            className="custom_cmed_mobile_button"
            disabled={!hasUserArranged}
          >
            APPLY CHANGES
          </IonButton>
        </IonFooter>
      </IonModal>
      <IonAlert
        isOpen={isCloseAlertOpen}
        header={"Discard Changes?"}
        message={
          "The changes will be lost if the screen is closed without applying."
        }
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
            handler: () => setIsCloseAlertOpen(false),
          },
          {
            text: "Discard",
            handler: () => setIsOpen(false),
          },
        ]}
        onDidDismiss={() => setIsCloseAlertOpen(false)}
      ></IonAlert>
      <IonToast
        isOpen={isArrangeSaved}
        message="New arrangement is set."
        onDidDismiss={() => setIsArrangeSaved(false)}
        duration={5000}
        color="success"
        icon={checkmarkCircleOutline}
        buttons={[
          {
            text: "Dismiss",
            role: "cancel",
          },
        ]}
      ></IonToast>
    </Fragment>
  );
};

export const EditGridsModal = memo(EditGridsModalComponent);
